// Short pleasant sounds using Web Audio API. No assets needed.
// Every function is a no-op when audio is globally muted or when the
// browser blocks it (silent failure — never crashes the caller).

const SOUND_PREF_KEY = "spectraloom_sound_on";

export function isSoundEnabled() {
  try {
    // Default ON — user must explicitly disable.
    return localStorage.getItem(SOUND_PREF_KEY) !== "0";
  } catch {
    return true;
  }
}

export function setSoundEnabled(enabled) {
  try {
    localStorage.setItem(SOUND_PREF_KEY, enabled ? "1" : "0");
  } catch {
    /* ignore */
  }
}

// ---- Internal helper -------------------------------------------------------
// Runs `schedule(ctx, now, play)` where `play(freq, start, dur, gain, type)`
// enqueues a single oscillator. Auto-closes the context when done.
function _run(schedule, closeAfterMs = 500) {
  if (!isSoundEnabled()) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const play = (freq, start = 0, dur = 0.14, gain = 0.14, type = "sine") => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, now + start);
      g.gain.exponentialRampToValueAtTime(gain, now + start + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(g).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    };
    schedule(ctx, now, play);
    setTimeout(() => {
      try { ctx.close(); } catch { /* ignore */ }
    }, closeAfterMs);
  } catch {
    /* silent */
  }
}

// ---- Analysis-complete (legacy `playBeep`) ---------------------------------
// Two-tone chime E5 → A5 — used when the /analyze job finishes.
export function playBeep() {
  _run((ctx, now, play) => {
    play(659.25, 0, 0.18, 0.18, "sine");
    play(880.0, 0.12, 0.18, 0.18, "sine");
  }, 500);
}

// ---- Optimization-complete -------------------------------------------------
// Warm triangle-wave C major arpeggio C5→E5→G5→C6 — clearly distinct from
// the analysis chime above.
export function playBeepOptimize() {
  _run((ctx, now, play) => {
    play(523.25, 0.00, 0.22, 0.16, "triangle");
    play(659.25, 0.10, 0.22, 0.16, "triangle");
    play(783.99, 0.20, 0.22, 0.16, "triangle");
    play(1046.5, 0.32, 0.32, 0.16, "triangle");
  }, 900);
}

// ---- Upload complete -------------------------------------------------------
// Bright rising "swoop" G5 → C6 → E6 — signals "file received".
export function playUploadDone() {
  _run((ctx, now, play) => {
    play(783.99, 0.00, 0.10, 0.14, "sine");
    play(1046.5, 0.07, 0.10, 0.14, "sine");
    play(1318.5, 0.14, 0.14, 0.14, "sine");
  }, 500);
}

// ---- Download start --------------------------------------------------------
// Short descending "packing" click D6 → G5 — signals "sending you the file".
export function playDownloadStart() {
  _run((ctx, now, play) => {
    play(1174.7, 0.00, 0.07, 0.12, "square");
    play(783.99, 0.06, 0.10, 0.12, "square");
  }, 400);
}

// ---- Button click: LUFS preset --------------------------------------------
// Mid-tone soft "tick" at A5 — feels like a physical selector click.
export function playClickLufs() {
  _run((ctx, now, play) => {
    play(880.0, 0.00, 0.05, 0.10, "sine");
  }, 200);
}

// ---- Button click: Reference toggle ---------------------------------------
// Dual-tone: rising (on) or falling (off) two-note switch.
export function playClickReference(on = true) {
  _run((ctx, now, play) => {
    if (on) {
      play(659.25, 0.00, 0.06, 0.11, "sine"); // E5
      play(987.77, 0.05, 0.09, 0.11, "sine"); // B5
    } else {
      play(987.77, 0.00, 0.06, 0.09, "sine"); // B5
      play(659.25, 0.05, 0.09, 0.09, "sine"); // E5
    }
  }, 250);
}

// ---- Button click: Genre selector -----------------------------------------
// Bright high tick at C6 with a hint of a second harmonic — feels crisp
// and matches the "musical" mood of picking a genre.
export function playClickGenre() {
  _run((ctx, now, play) => {
    play(1046.5, 0.00, 0.05, 0.09, "triangle"); // C6
    play(1568.0, 0.02, 0.05, 0.06, "triangle"); // G6
  }, 200);
}
